import { formatInLondon, parseGraphqlTimestamptz } from "./uk-datetime.ts";
import { isCustomerHiddenAssessment } from "../lib/customer-assessment-privacy.ts";
import {
  getCourseScheduleDates,
  getPublicCourseSchedule,
  type WebsiteScheduleEvent,
} from "../lib/course-schedule.ts";
import {
  filterClassScheduleDates,
  type ScheduleDateEntry,
  type TimedScheduleDateEntry,
} from "./theory-exam-schedule.ts";

/** VTCT schedule rows with this `resource_id` are shown under Online Webinar. */
export const VTCT_ONLINE_WEBINAR_RESOURCE_ID =
  "9fe3d594-5bca-45c3-a5a7-3cae6beac29d";

/**
 * Single popover line for a class schedule entry.
 * Returns null when start is missing or not a parseable timestamp.
 * Omits the end-time suffix when end is missing/invalid (no trailing " - ").
 */
export function formatSchedulePopoverDateLine(
  entry: Pick<ScheduleDateEntry, "start" | "end"> | null | undefined,
): string | null {
  if (!entry) return null;

  const startLabel = formatInLondon(entry.start, "EEEE do MMMM p");
  if (!startLabel) return null;

  const endLabel = formatInLondon(entry.end, "p");
  return endLabel ? `${startLabel} - ${endLabel}` : startLabel;
}

export type CourseSchedulePopoverSections = {
  webinarLines: string[];
  practicalLines: string[];
  webinarDates: TimedScheduleDateEntry[];
  practicalDates: TimedScheduleDateEntry[];
};

/**
 * Class-day schedule rows for the "View Full Course Schedule" popover:
 * theory / remote exam slots are excluded; webinar vs practical split by
 * resource_id; display lines tolerate minute-only CRM timestamps and missing ends.
 */
export function getCourseSchedulePopoverSections(
  dates: Array<ScheduleDateEntry | null | undefined> | null | undefined,
): CourseSchedulePopoverSections {
  const classDates = filterClassScheduleDates(dates).filter((row) => !isCustomerHiddenAssessment(row));
  const webinarDates = classDates.filter(
    (d) => d.resource_id === VTCT_ONLINE_WEBINAR_RESOURCE_ID,
  );
  const practicalDates = classDates.filter(
    (d) => d.resource_id !== VTCT_ONLINE_WEBINAR_RESOURCE_ID,
  );

  return {
    webinarDates,
    practicalDates,
    webinarLines: webinarDates
      .map(formatSchedulePopoverDateLine)
      .filter((line): line is string => line != null),
    practicalLines: practicalDates
      .map(formatSchedulePopoverDateLine)
      .filter((line): line is string => line != null),
  };
}


/** Earliest actual delivery, including online teaching before onsite attendance. */
export function courseScheduleStart(event: WebsiteScheduleEvent | null | undefined): string | null {
  const result = getPublicCourseSchedule(event);
  if (!result.ok) return null;
  return result.schedule.firstTeachingStart ?? result.schedule.delivery[0]?.start ?? null;
}

/** London date matching deliberately compares the delivery interval, never the course span. */
export function scheduleDatesForDay(event: WebsiteScheduleEvent | null | undefined, day: Date | string) {
  const key = typeof day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : formatInLondon(day, "yyyy-MM-dd");
  if (!key) return [];
  return getCourseScheduleDates(event).filter((entry) => {
    const start = formatInLondon(entry.start, "yyyy-MM-dd");
    // Delivery intervals are half-open, so an end at midnight belongs to the preceding day.
    const endInstant = parseGraphqlTimestamptz(entry.end).getTime();
    const end = formatInLondon(new Date(endInstant - 1), "yyyy-MM-dd");
    return start && end && start <= key && key <= end;
  });
}

/** Year tabs group each course once, by its first teaching date in London. */
export function courseScheduleYears(event: WebsiteScheduleEvent | null | undefined): number[] {
  const year = formatInLondon(courseScheduleStart(event), "yyyy");
  return year ? [Number(year)] : [];
}

/** Internal EPA arrangements never supply customer course dates. */
export function scheduleAssessmentLines(event: WebsiteScheduleEvent | null | undefined): string[] {
  const result = getPublicCourseSchedule(event);
  if (!result.ok) return [];
  return result.schedule.externalAssessments.map((assessment) => {
    const label = assessment.label || "End point assessment";
    const timing = assessment.start && assessment.end
      ? `${formatInLondon(assessment.start, "EEEE do MMMM yyyy p")} - ${formatInLondon(assessment.end, "p")}`
      : null;
    if (assessment.status === "CANCELLED") return `${label}: cancelled`;
    if (assessment.status === "COMPLETED") return `${label}: completed`;
    if (timing) return `${label}: ${timing}. Shared calendar ${assessment.status === "HOLD" ? "hold" : "event"}; your appointment will be arranged separately.`;
    return `${label}: date to be arranged separately.`;
  });
}

/** Customer teaching dates exclude both shared and old inline EPA dates. */
export function getEventCourseSchedulePopoverSections(event: WebsiteScheduleEvent | null | undefined): CourseSchedulePopoverSections & { assessmentLines: string[] } {
  const dates = getCourseScheduleDates(event);
  const webinarDates = dates.filter((entry) => entry.kind === "WEBINAR");
  const practicalDates = dates.filter((entry) => entry.kind !== "WEBINAR");
  return {
    webinarDates,
    practicalDates,
    webinarLines: webinarDates.map(formatSchedulePopoverDateLine).filter((line): line is string => line != null),
    practicalLines: practicalDates.map(formatSchedulePopoverDateLine).filter((line): line is string => line != null),
    assessmentLines: scheduleAssessmentLines(event),
  };
}

/** Preserve the catalogue's existing policy: earlier cohorts remain visible in All dates. */
export function courseStartsBeforeToday(event: WebsiteScheduleEvent | null | undefined, now = new Date()): boolean {
  const first = courseScheduleStart(event);
  return !!first && formatInLondon(first, "yyyy-MM-dd") < formatInLondon(now, "yyyy-MM-dd");
}
